import { useState, useEffect } from "react";
import { useLocation, Link } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { GoogleLogin } from "@react-oauth/google";
import { useIsLoading, useLogin, useLoginWith2fa, useRequires2fa, useClearTwoFactor, useLoginWithGoogle } from "@/stores/auth.store";
import { getGoogleClientId } from "@/lib/env";
import FooterPublic from "@/components/layout/footer-public";

const PARTNERS = [
  { name: "Pátria Investimentos", logo: "/assets/login/partners/patria.png", size: "small" as const },
  { name: "Galápagos Capital", logo: "/assets/login/partners/galapagos.png", size: "normal" as const },
  { name: "Itaú", logo: "/assets/login/partners/itau.png", size: "large" as const },
  { name: "Honey Island", logo: "/assets/login/partners/honey-island.png", size: "large" as const },
  { name: "Oliveira Trust", logo: "/assets/login/partners/oliveira-trust.png", size: "normal" as const },
];

function EyeIcon({ visible }: { visible: boolean }) {
  if (visible) {
    return (
      <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
      </svg>
    );
  }
  return (
    <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12c1.292 4.338 5.31 7.5 10.066 7.5.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
    </svg>
  );
}

function TwoFactorModal({ onSubmit, onCancel, isLoading }: { onSubmit: (code: string) => void; onCancel: () => void; isLoading: boolean }) {
  const [code, setCode] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (code.trim().length === 6) {
      onSubmit(code.trim());
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-sm mx-4">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-neutral-900">Verificação em duas etapas</h3>
          <p className="text-sm text-neutral-500 mt-1">
            Digite o código de 6 dígitos do seu app autenticador
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="w-full py-3 px-4 border border-neutral-200 rounded-lg text-center text-2xl font-mono tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent"
            style={{ backgroundColor: "#ffffff", color: "#171717" }}
            autoFocus
            disabled={isLoading}
          />

          <button
            type="submit"
            disabled={isLoading || code.length !== 6}
            className="w-full py-3 px-6 rounded-lg text-white font-semibold text-base transition-all disabled:opacity-50"
            style={{ backgroundColor: "#0247FE" }}
          >
            {isLoading ? "Verificando..." : "Verificar"}
          </button>

          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            Voltar
          </button>
        </form>
      </div>
    </div>
  );
}

export default function Login() {
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const login = useLogin();
  const loginWithGoogle = useLoginWithGoogle();
  const loginWith2fa = useLoginWith2fa();
  const isLoading = useIsLoading();
  const requires2fa = useRequires2fa();
  const clearTwoFactor = useClearTwoFactor();
  const searchParams = new URLSearchParams(window.location.search);
  const redirectTo = searchParams.get("redirect") || "/";
  const reason = searchParams.get("reason");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(false);
  const [ssoMode, setSsoMode] = useState(false);
  const [ssoEmail, setSsoEmail] = useState("");
  const googleClientId = getGoogleClientId();

  useEffect(() => {
    if (reason === "inactivity") {
      toast({
        title: "Sessão expirada",
        description: "Você foi desconectado por inatividade. Faça login novamente.",
        variant: "destructive",
      });
    }
  }, [reason, toast]);

  const handleSsoLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ssoEmail.trim()) return;
    setSsoLoading(true);
    try {
      let recaptchaToken = "";
      try {
        if (executeRecaptcha) {
          recaptchaToken = await executeRecaptcha("sso_login");
        }
      } catch {
        // reCAPTCHA não disponível (dev/E2E)
      }
      const apiUrl = import.meta.env.VITE_API_URL || "";
      const resp = await fetch(`${apiUrl}/auth/sso/url`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(recaptchaToken ? { "x-recaptcha-token": recaptchaToken } : {}),
        },
        body: JSON.stringify({ email: ssoEmail.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast({ title: "SSO indisponível", description: data.message || "Erro ao obter URL SSO", variant: "destructive" });
        return;
      }
      if (data.url) {
        window.location.href = data.url;
      }
    } catch {
      toast({ title: "Erro", description: "Falha ao conectar com SSO", variant: "destructive" });
    } finally {
      setSsoLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) return;

    let recaptchaToken = "";
    try {
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("login");
      }
    } catch {
      // reCAPTCHA não disponível (dev/E2E) — continua sem token
    }

    const result = await login(email.trim(), password, toast, recaptchaToken);
    if (result) {
      setLocation(redirectTo);
    }
    // se requires2fa ficou true, o modal aparece automaticamente
  };

  const handle2faSubmit = async (code: string) => {
    const result = await loginWith2fa(code, toast);
    if (result) {
      setLocation(redirectTo);
    }
  };

  const handle2faCancel = () => {
    clearTwoFactor();
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* 2FA Modal */}
      {requires2fa && (
        <TwoFactorModal onSubmit={handle2faSubmit} onCancel={handle2faCancel} isLoading={isLoading} />
      )}

      {/* Left Side - Login */}
      <div className="w-full lg:w-1/2 flex flex-col px-4 sm:px-8 md:px-16 lg:px-24 py-8 sm:py-12 lg:py-0 bg-white min-h-screen lg:min-h-0">
        <div className="flex-1 flex items-center justify-center">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="flex justify-start mb-6">
            <img
              src="/assets/login/image-logo.svg"
              alt="Liqi - Portal do cliente"
              className="h-12 sm:h-14 w-auto"
              data-testid="img-logo-login"
            />
          </div>

          {/* Welcome Text */}
          <div className="mb-8">
            <h1 style={{ fontSize: "30px", lineHeight: "36px" }} className="font-semibold text-neutral-900 mb-2">
              {ssoMode ? "Login SSO" : "Bem-vindo de volta"}
            </h1>
            <p style={{ fontSize: "16px", lineHeight: "24px" }} className="text-neutral-500">
              {ssoMode
                ? "Digite seu email corporativo para entrar via Microsoft"
                : "Entre com suas credenciais para acessar a plataforma"}
            </p>
          </div>

          {ssoMode ? (
            /* SSO Form */
            <form onSubmit={handleSsoLogin} className="flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="sso-email" className="text-sm font-medium text-neutral-900">
                  Email corporativo
                </label>
                <input
                  id="sso-email"
                  type="email"
                  placeholder="seu@empresa.com"
                  value={ssoEmail}
                  onChange={(e) => setSsoEmail(e.target.value)}
                  className="w-full py-3 px-4 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-transparent"
                  style={{ backgroundColor: "#ffffff", color: "#171717" }}
                  required
                  autoFocus
                  disabled={ssoLoading}
                />
              </div>

              <button
                type="submit"
                disabled={ssoLoading || !ssoEmail.trim()}
                className="w-full py-3 px-6 rounded-lg text-white font-semibold text-sm transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: "#0247FE" }}
              >
                <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none">
                  <path d="M11 0H0V11H11V0Z" fill="#F25022"/>
                  <path d="M23 0H12V11H23V0Z" fill="#7FBA00"/>
                  <path d="M11 12H0V23H11V12Z" fill="#00A4EF"/>
                  <path d="M23 12H12V23H23V12Z" fill="#FFB900"/>
                </svg>
                {ssoLoading ? "Redirecionando..." : "Continuar com Microsoft"}
              </button>

              <button
                type="button"
                onClick={() => setSsoMode(false)}
                className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors text-center"
              >
                ← Voltar ao login com senha
              </button>
            </form>
          ) : (
          /* Normal Login Form */
          <form onSubmit={handleLogin} className="flex flex-col gap-5">
            {/* Email */}
            <div className="flex flex-col gap-1.5">
              <label htmlFor="email" className="text-sm font-medium text-neutral-900">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="Seu e-mail corporativo"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full py-3 px-4 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-transparent"
                style={{ backgroundColor: "#ffffff", color: "#171717" }}
                required
                disabled={isLoading}
              />
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="text-sm font-medium text-neutral-900">
                  Senha
                </label>
                <Link
                  href="/esqueci-senha"
                  className="text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
                >
                  Esqueceu a senha?
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Sua senha"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full py-3 px-4 pr-12 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-neutral-300 focus:border-transparent"
                  style={{ backgroundColor: "#ffffff", color: "#171717" }}
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 rounded transition-colors"
                >
                  <EyeIcon visible={showPassword} />
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3 px-6 rounded-lg text-white font-semibold text-base transition-all disabled:opacity-50"
              style={{ backgroundColor: "#0247FE" }}
            >
              {isLoading ? "Autenticando..." : "Entrar"}
            </button>

            {/* SSO Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-neutral-200" />
              <span className="text-xs text-neutral-600 uppercase tracking-wider">ou</span>
              <div className="flex-1 h-px bg-neutral-200" />
            </div>

            {/* SSO Button */}
            <button
              type="button"
              onClick={() => setSsoMode(true)}
              className="w-full py-3 px-6 rounded-lg font-semibold text-sm transition-all border border-neutral-200 hover:bg-neutral-50 flex items-center justify-center gap-2"
              style={{ backgroundColor: "#ffffff", color: "#171717" }}
            >
              <svg className="w-5 h-5" viewBox="0 0 23 23" fill="none">
                <path d="M11 0H0V11H11V0Z" fill="#F25022"/>
                <path d="M23 0H12V11H23V0Z" fill="#7FBA00"/>
                <path d="M11 12H0V23H11V12Z" fill="#00A4EF"/>
                <path d="M23 12H12V23H23V12Z" fill="#FFB900"/>
              </svg>
              Entrar com SSO (Microsoft)
            </button>

            {/* Google Login — só renderiza quando VITE_GOOGLE_CLIENT_ID está definido */}
            {googleClientId && (
              <div className="flex justify-center" data-testid="google-login-btn">
                <GoogleLogin
                  onSuccess={(credentialResponse) => {
                    if (credentialResponse.credential) {
                      loginWithGoogle(credentialResponse.credential, toast).then((result) => {
                        if (result) setLocation(redirectTo);
                      });
                    }
                  }}
                  onError={() => {
                    toast({ title: "Erro", description: "Falha ao autenticar com Google", variant: "destructive" });
                  }}
                  text="signin_with"
                  shape="rectangular"
                  width={400}
                />
              </div>
            )}
          </form>
          )}

          {/* Footer */}
          <p className="text-center text-sm text-neutral-500 mt-6">
            Não tem uma conta?{" "}
            <span className="text-neutral-900 font-semibold cursor-pointer hover:underline">
              Solicite acesso ao administrador
            </span>
          </p>
        </div>
        </div>
        <FooterPublic />
      </div>

      {/* Right Side - Branding Panel (dark) */}
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center p-8 lg:p-12 relative overflow-hidden" style={{ backgroundColor: "#111218" }}>
        {/* Content */}
        <div className="relative z-10 max-w-2xl mx-auto w-full flex flex-col justify-between min-h-[60vh]">
          {/* Top Section - Logo and Headline */}
          <div className="text-center">
            {/* Logo */}
            <div className="flex flex-col items-center justify-center gap-2 mb-12">
              <div className="flex items-center justify-center gap-2">
                <img
                  src="/assets/logo-sidebar.svg"
                  alt="Liqi"
                  className="h-8 lg:h-10 w-auto brightness-0 invert"
                />
                <span className="text-white text-2xl lg:text-3xl font-light">caas</span>
              </div>
              <span className="text-white/60 text-sm">crypto as a service</span>
            </div>

            {/* Headline */}
            <div className="mb-12">
              <h2 style={{ fontSize: "36px", lineHeight: "44px" }} className="font-semibold text-white mb-4">
                Sua operação cripto
                <br />
                sob controle total
              </h2>
              <p style={{ fontSize: "16px", lineHeight: "28px" }} className="text-white/60 max-w-md mx-auto">
                Acompanhe transações, clientes, saldos e tesouraria
                em tempo real — do resumo executivo ao detalhe da ordem, num só portal.
              </p>
            </div>

            {/* Feature Tags */}
            <div className="flex flex-wrap justify-center gap-2">
              {[
                "Resumo",
                "Transações",
                "Clientes",
                "Extrato de Saldos",
                "Solicitações",
                "Saque",
                "Tesouraria",
              ].map((tag) => (
                <span
                  key={tag}
                  className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-medium text-white/80"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Bottom Section - Partners */}
          <div className="mt-auto pt-12">
            <p className="text-xs text-white/40 text-center mb-6 lg:mb-8 font-medium uppercase tracking-wider">
              Sócios e Parceiros
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 lg:gap-8">
              {PARTNERS.map((partner) => (
                <div
                  key={partner.name}
                  className={`flex items-center justify-center opacity-60 hover:opacity-90 transition-all duration-300 ${
                    partner.size === "large"
                      ? "h-10 lg:h-12"
                      : partner.size === "small"
                        ? "h-8 lg:h-10"
                        : "h-6 lg:h-8"
                  }`}
                >
                  <img
                    src={partner.logo}
                    alt={`Logo ${partner.name}`}
                    className={`w-auto object-contain brightness-0 invert ${
                      partner.size === "large"
                        ? "max-h-10 lg:max-h-12"
                        : partner.size === "small"
                          ? "max-h-8 lg:max-h-10"
                          : "max-h-5 lg:max-h-6"
                    }`}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
